import { Box } from '@mui/material';
import { useForm, SubmitHandler } from 'react-hook-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CancellationSubType,
  MidtermTransactionInput,
  RefundType,
  TransactionStatus,
  useUpsertMidtermTransaction,
} from '@converge/graphql';

import { ApolloError } from '@apollo/client';
import { CreateMidtermTransactionField } from '@converge/data';
import { CreateMidtermTransactionFieldsSchema } from './midterm-transaction.validation.schema';
import { MidtermTransactionNote } from './midterm-transaction-note';
import { ModalStep } from '../insured-policies.detail';
import { NoActiveClaimsConfirmation } from './active-claims-modal';
import { CancellationDetails } from './cancellation-details';
import moment from 'moment';
import { useConvergeSnackBar } from '../../../../contexts';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  activeClaimStepConfig,
  MidtermTransactionModalProps,
  ModalConfigType,
  ModalLabels,
  stepValues,
  transactionNoteStepConfig,
} from './midterm-transaction-modal';
import { MidtermTransactionModal } from './midterm-transaction-modal';
import { useModalStepper } from './midterm-transaction-modal.hooks';

export const MidtermTransactionModalCancellation = ({
  policyUuid,
  effectiveDate,
  maxDate,
  transactionType,
  onClose,
  showClaimMessage,
  hasOpenClaims = false,
  claimsLoading = false,
}: MidtermTransactionModalProps) => {
  const { notify } = useConvergeSnackBar();
  const [noActiveClaims, setNoActiveClaims] = useState(false);
  const isOpen = useMemo(() => policyUuid !== '', [policyUuid]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [currentFirstStep, setCurrentFirstStep] = useState<ModalStep>(
    ModalStep.CANCELLATION_DETAILS
  );

  useEffect(() => {
    const newFirstStep =
      !claimsLoading && hasOpenClaims
        ? ModalStep.ACTIVE_CLAIM_INFO
        : ModalStep.CANCELLATION_DETAILS;
    setCurrentFirstStep(newFirstStep);
  }, [claimsLoading, hasOpenClaims]);

  const formMethods = useForm<CreateMidtermTransactionFields>({
    resolver: yupResolver(CreateMidtermTransactionFieldsSchema),
    defaultValues: {},
  });
  const onValid: SubmitHandler<CreateMidtermTransactionFields> = async (
    fields
  ) => {
    const { cancellationType, startDate, refundType, notes } = fields;
    if (!policyUuid || !cancellationType || !startDate || !refundType || !notes)
      return;

    await onSubmitMidtermTransactionHandler({
      type: transactionType,
      subType: fields.cancellationType,
      effectiveDate: moment(fields.startDate)
        .utc()
        .format('YYYY-MM-DDTHH:mm:ssZ'),
      refundType: fields.refundType,
      notes: fields.notes,
      policyUuid: policyUuid,
      status: TransactionStatus.Completed,
    });
  };

  const handleOnClose = useCallback(() => {
    formMethods.reset();
    onClose();
    setNoActiveClaims(false);
  }, [onClose, formMethods]);
  const submitForm = formMethods.handleSubmit(onValid);
  const modalConfig: ModalConfigType = {
    ...activeClaimStepConfig(
      <NoActiveClaimsConfirmation
        isChecked={noActiveClaims}
        setIsChecked={setNoActiveClaims}
        setIsDisabled={setIsDisabled}
      />,
      ModalStep.CANCELLATION_DETAILS
    ),
    [ModalStep.CANCELLATION_DETAILS]: {
      title: <Box className="modal-title">Cancellation Details</Box>,
      content: (
        <CancellationDetails
          setIsDisabled={setIsDisabled}
          effectiveDate={effectiveDate}
          maxDate={maxDate}
        />
      ),
      prevLabel: ModalLabels.BACK,
      nextLabel: ModalLabels.NEXT,
      nextStep: ModalStep.TRANSACTION_NOTE,
    },
    ...transactionNoteStepConfig(
      <MidtermTransactionNote setIsDisabled={setIsDisabled} />
    ),
  };

  const { currentStep, handleNextPage, handleBackPage } = useModalStepper({
    modalConfig,
    firstStep: currentFirstStep,
    onClose: handleOnClose,
    setIsDisabled,
    onFinalStepSubmit: submitForm,
    hasOpenClaims,
  });

  interface CreateMidtermTransactionFields {
    [CreateMidtermTransactionField.cancellationType]:
      | CancellationSubType
      | undefined;
    [CreateMidtermTransactionField.refundType]: RefundType;
    [CreateMidtermTransactionField.notes]: string | undefined;
    [CreateMidtermTransactionField.startDate]: string | undefined;
  }

  const { upsertMidtermTransaction, loading } = useUpsertMidtermTransaction();

  const onSubmitMidtermTransactionHandler = useCallback(
    async (props: MidtermTransactionInput) => {
      try {
        if (!props.notes) return;
        await upsertMidtermTransaction({
          variables: {
            input: {
              ...props,
            },
          },
        });
        notify(`Midterm transaction created successfully`, {
          variant: 'success',
        });
        handleOnClose();
      } catch (cause) {
        if (cause instanceof ApolloError) {
          const errorMessage = cause.graphQLErrors[0].message;
          notify(
            `Failed to upsert midterm transaction: ${
              errorMessage || 'No data returned by the API'
            }`,
            {
              variant: 'error',
            }
          );
        }
        console.error(`Failed to upsert midterm transaction: ${cause}`);
      }
    },
    [notify, upsertMidtermTransaction, handleOnClose]
  );

  const { title, body, prevButtonLabel, nextButtonLabel } = stepValues(
    currentStep,
    modalConfig[currentStep]
  );

  return (
    <MidtermTransactionModal
      isOpen={isOpen}
      formMethods={formMethods}
      onClose={handleOnClose}
      title={title}
      showClaimMessage={showClaimMessage}
      body={body}
      prevButtonLabel={prevButtonLabel}
      nextButtonLabel={nextButtonLabel}
      onBack={handleBackPage}
      onNext={handleNextPage}
      isNextDisabled={isDisabled}
      isLoading={loading}
      claimsLoading={claimsLoading}
    />
  );
};
